import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import Modal from '../components/Modal';
import { adminApi, type AdminPaymentMethodsOverview } from '../services/api';
import { apiErrorText } from '../utils/apiError';

type Method = AdminPaymentMethodsOverview['methods'][number];

const REASON_MAX_LENGTH = 500;

/**
 * Adyen › Betalmetoder (ClickUp 869fnhhhb): Admin switches the deposit methods
 * on and off centrally, with a reason. A switched-off method cannot start a
 * new deposit; one already started with the payment partner may finish.
 * Method names come from data and are not translated.
 */
export function AdminAdyenPaymentMethods() {
  const { t, i18n } = useTranslation();
  const [overview, setOverview] = useState<AdminPaymentMethodsOverview | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [confirming, setConfirming] = useState<Method | null>(null);
  const [reason, setReason] = useState('');
  const [allOffConfirmed, setAllOffConfirmed] = useState(false);
  const [tried, setTried] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    adminApi.getPaymentMethods().then(setOverview).catch(() => setLoadError(true));
  }, []);

  const methods = overview?.methods ?? [];
  const nameOf = (key: string) => methods.find(method => method.method === key)?.name ?? key;
  const switchedOn = (method: Method) => method.enabled && method.allowedByEnvironment;
  const offering = methods.filter(switchedOn);
  // Switching off the last method a deposit can use stops every deposit.
  const lastMethod = !!confirming && confirming.enabled && offering.length === 1 && offering[0].method === confirming.method;
  const reasonMissing = !reason.trim();
  const dateTime = (value: string) => new Date(value).toLocaleString(i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });

  const open = (method: Method) => {
    setConfirming(method);
    setReason('');
    setAllOffConfirmed(false);
    setTried(false);
    setSaveError('');
  };

  const save = async () => {
    if (!confirming) return;
    setTried(true);
    if (reasonMissing || (lastMethod && !allOffConfirmed)) return;
    setIsSaving(true);
    setSaveError('');
    try {
      const enabled = !confirming.enabled;
      setOverview(await adminApi.updatePaymentMethod(confirming.method, { enabled, reason: reason.trim() }));
      setMessage(t(enabled ? 'adminPaymentMethods.switchedOn' : 'adminPaymentMethods.switchedOff', { name: confirming.name }));
      setConfirming(null);
    } catch (failure) {
      setSaveError(apiErrorText(t, failure, 'adminPaymentMethods.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminPaymentMethods.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('appNav.adyen') },
            { label: t('adminPaymentMethods.title') }
          ]} />
        </div>
      </div>

      {message && (
        <div className="alert alert-success alert-dismissible" role="status">
          {message}
          <button type="button" className="btn-close" aria-label={t('common.close')} onClick={() => setMessage('')}></button>
        </div>
      )}

      {overview && offering.length === 0 && (
        <div className="alert alert-danger" role="alert" data-testid="admin-payment-methods-all-off">
          {t('adminPaymentMethods.allOff')}
        </div>
      )}

      <Card className="mb-4">
        <p className="text-explain">{t('adminPaymentMethods.description')}</p>
        {!overview && !loadError && (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}
        {loadError && <div className="alert alert-danger mb-0" role="alert">{t('adminPaymentMethods.loadError')}</div>}
        {overview && (
          <ul className="list-unstyled mb-0" data-testid="admin-payment-methods">
            {methods.map(method => (
              <li
                key={method.method}
                className={`admin-payment-method border rounded p-3 mb-2 ${switchedOn(method) ? '' : 'is-off'}`}
                data-testid={`admin-payment-method-${method.method}`}
              >
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div>
                    <h6 className="fw-bold mb-1">{method.name}</h6>
                    <span className={`badge ${method.enabled ? 'bg-success' : 'bg-secondary'}`}>
                      {t(method.enabled ? 'adminPaymentMethods.status.on' : 'adminPaymentMethods.status.off')}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`btn admin-payment-method-toggle ${method.enabled ? 'btn-outline-danger' : 'btn-outline-success'}`}
                    onClick={() => open(method)}
                    data-testid={`admin-payment-method-toggle-${method.method}`}
                  >
                    {t(method.enabled ? 'adminPaymentMethods.actions.switchOff' : 'adminPaymentMethods.actions.switchOn')}
                  </button>
                </div>
                {!method.allowedByEnvironment && (
                  <p className="text-explain mb-0 mt-2">{t('adminPaymentMethods.notInEnvironment')}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {overview && (
        <Card>
          <h6 className="fw-bold mb-3">{t('adminPaymentMethods.history.title')}</h6>
          {overview.history.length === 0 ? (
            <p className="text-explain mb-0">{t('adminPaymentMethods.history.empty')}</p>
          ) : (
            <ul className="list-unstyled mb-0" data-testid="admin-payment-methods-history">
              {overview.history.map(entry => (
                <li key={`${entry.changedAt}-${entry.method}`} className="border-bottom py-2">
                  <div className="d-flex flex-wrap gap-2 align-items-baseline">
                    <span className="fw-semibold">
                      {t(entry.enabled ? 'adminPaymentMethods.history.switchedOn' : 'adminPaymentMethods.history.switchedOff', { name: nameOf(entry.method) })}
                    </span>
                    <span className="text-muted small">
                      {entry.changedBy
                        ? t('adminPaymentMethods.history.byAt', { name: entry.changedBy, at: dateTime(entry.changedAt) })
                        : dateTime(entry.changedAt)}
                    </span>
                  </div>
                  <p className="text-explain mb-0">{entry.reason}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {confirming && (
        <Modal
          title={t(confirming.enabled ? 'adminPaymentMethods.confirm.offTitle' : 'adminPaymentMethods.confirm.onTitle', { name: confirming.name })}
          onClose={() => { if (!isSaving) setConfirming(null); }}
          closeAriaLabel={t('common.close')}
          footer={
            <div className="modal-action-row">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(null)} disabled={isSaving}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className={`btn ${confirming.enabled ? 'btn-danger' : 'btn-success'}`}
                onClick={() => void save()}
                disabled={isSaving}
                data-testid="admin-payment-method-confirm"
              >
                {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                {t(confirming.enabled ? 'adminPaymentMethods.actions.switchOff' : 'adminPaymentMethods.actions.switchOn')}
              </button>
            </div>
          }
        >
          {saveError && <div className="alert alert-danger" role="alert">{saveError}</div>}
          <p>
            {t(confirming.enabled ? 'adminPaymentMethods.confirm.offBody' : 'adminPaymentMethods.confirm.onBody', { name: confirming.name })}
          </p>
          {lastMethod && (
            <div className="alert alert-warning" role="status">
              <p className="mb-2">{t('adminPaymentMethods.confirm.lastMethod')}</p>
              <div className="form-check">
                <input
                  id="admin-payment-method-all-off"
                  type="checkbox"
                  className={`form-check-input ${tried && !allOffConfirmed ? 'is-invalid' : ''}`}
                  checked={allOffConfirmed}
                  onChange={event => setAllOffConfirmed(event.target.checked)}
                />
                <label className="form-check-label" htmlFor="admin-payment-method-all-off">
                  {t('adminPaymentMethods.confirm.allOffCheck')}
                </label>
              </div>
            </div>
          )}
          <label className="form-label fw-semibold" htmlFor="admin-payment-method-reason">
            {t('adminPaymentMethods.confirm.reason')} *
          </label>
          <textarea
            id="admin-payment-method-reason"
            className={`form-control ${tried && reasonMissing ? 'is-invalid' : ''}`}
            rows={3}
            maxLength={REASON_MAX_LENGTH}
            value={reason}
            placeholder={t('adminPaymentMethods.confirm.reasonPlaceholder')}
            onChange={event => setReason(event.target.value)}
            aria-describedby="admin-payment-method-reason-help"
          />
          {tried && reasonMissing && <div className="invalid-feedback">{t('adminPaymentMethods.confirm.reasonRequired')}</div>}
          <p className="text-explain mt-2 mb-0" id="admin-payment-method-reason-help">{t('adminPaymentMethods.confirm.reasonHelp')}</p>
        </Modal>
      )}
    </DashboardLayout>
  );
}

export default AdminAdyenPaymentMethods;
